import { RHFControl, RHFPhoneInput, RHFSelect } from 'components/common/form';
import { Card, Col, Container, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

const relationshipOptions = [
  { label: 'Parent', value: 'Parent' },
  { label: 'Relative', value: 'Relative' },
  { label: 'Guardian', value: 'Guardian' },
  { label: 'Sibling', value: 'Sibling' },
  { label: 'Other', value: 'Other' }
];

export const GuardianDetails = ({
  sectionRef,
  sideNavLink,
  locationCountry
}) => {
  const { watch, setValue } = useFormContext();
  const father = watch('parents.father');
  const mother = watch('parents.mother');
  const inheritFrom = watch('guardian.inheritFrom');

  const inheritOptions = useMemo(() => {
    const opts = [{ label: 'None (enter manually)', value: '' }];
    const fatherName = [father?.first_name, father?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    const motherName = [mother?.first_name, mother?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (fatherName || father?.phone) {
      opts.push({
        label: `Father${fatherName ? ` — ${fatherName}` : ''}`,
        value: 'father'
      });
    }
    if (motherName || mother?.phone) {
      opts.push({
        label: `Mother${motherName ? ` — ${motherName}` : ''}`,
        value: 'mother'
      });
    }
    return opts;
  }, [father, mother]);

  // When user picks a parent to inherit from, copy that parent's fields into the guardian fields.
  useEffect(() => {
    if (inheritFrom !== 'father' && inheritFrom !== 'mother') return;
    const source = inheritFrom === 'father' ? father : mother;
    if (!source) return;
    setValue('guardian.firstName', source.first_name || '', {
      shouldDirty: true
    });
    setValue('guardian.middleName', source.middle_name || '', {
      shouldDirty: true
    });
    setValue('guardian.lastName', source.last_name || '', {
      shouldDirty: true
    });
    setValue('guardian.relationship', 'Parent', { shouldDirty: true });
    setValue('guardian.phone', source.phone || '', { shouldDirty: true });
    setValue('guardian.email', source.email || '', { shouldDirty: true });
    setValue('guardian.address', source.address || '', { shouldDirty: true });
    setValue('guardian.occupation', source.occupation || '', {
      shouldDirty: true
    });
    setValue('guardian.monthlyIncomeRange', source.monthly_income_range || '', {
      shouldDirty: true
    });
  }, [inheritFrom, father, mother, setValue]);

  return (
    <Card className="border shadow-none mb-3" ref={sectionRef} id={sideNavLink}>
      <Card.Header className="pb-0">
        <div className="dFlexRowStartSpace pt-3">
          <Card.Title as="h5">Current Guardian</Card.Title>
        </div>
      </Card.Header>
      <Card.Body>
        <Container className="p-0 d-flex flex-column g-3">
          <Row className="gx-3">
            <Col xs={12}>
              <RHFSelect
                name="guardian.inheritFrom"
                label="Use a parent as guardian"
                options={inheritOptions}
                helpText="Pick a parent to copy their details, or leave as 'None' to enter a different guardian."
              />
            </Col>
          </Row>
          <hr />
          <Row className="gx-3">
            <Col xs={12} sm={6}>
              <RHFControl name="guardian.firstName" label="First Name" />
            </Col>
            <Col xs={12} sm={6}>
              <RHFControl name="guardian.middleName" label="Middle Name" />
            </Col>
          </Row>
          <Row className="gx-3">
            <Col xs={12} sm={6}>
              <RHFControl name="guardian.lastName" label="Last Name" />
            </Col>
            <Col xs={12} sm={6}>
              <RHFSelect
                name="guardian.relationship"
                label="Relationship"
                options={relationshipOptions}
              />
            </Col>
          </Row>
          <Row className="gx-3">
            <Col xs={12} sm={6}>
              <RHFPhoneInput
                name="guardian.phone"
                label="Contact Number"
                defaultCountry={locationCountry}
              />
            </Col>
            <Col xs={12} sm={6}>
              <RHFControl name="guardian.email" label="Contact Email" />
            </Col>
          </Row>
          <Row className="gx-3">
            <Col xs={12} sm={6}>
              <RHFControl
                name="guardian.occupation"
                label="Occupation"
                placeholder="Guardian's occupation (optional)"
              />
            </Col>
            <Col xs={12} sm={6}>
              <RHFSelect
                name="guardian.monthlyIncomeRange"
                label="Monthly Income Range"
                options={[
                  { label: 'No Income', value: 'No Income' },
                  { label: 'Under £100', value: 'Under £100' },
                  { label: '£100 - £250', value: '£100 - £250' },
                  { label: '£250 - £500', value: '£250 - £500' },
                  { label: '£500 - £1000', value: '£500 - £1000' },
                  { label: '£1000 - £2000', value: '£1000 - £2000' },
                  { label: 'Above £2000', value: 'Above £2000' }
                ]}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <RHFControl
                name="guardian.address"
                as="textarea"
                rows={2}
                label="Address"
                placeholder="Guardian Address"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <RHFControl
                name="guardian.notes"
                label="Additional Comments / Notes"
                as="textarea"
                rows={3}
                placeholder="Any additional information about the guardian or family situation..."
              />
            </Col>
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
};

GuardianDetails.propTypes = {
  sectionRef: PropTypes.object,
  sideNavLink: PropTypes.string,
  locationCountry: PropTypes.string
};
